import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormHelperText,
  Grid,
  IconButton,
  makeStyles,
  Tooltip,
  Typography
} from '@material-ui/core'
import { Add, Close, Warning } from '@material-ui/icons'
import { isEqual } from 'lodash'
import { nanoid } from 'nanoid'
import { getSpeakersAvailablePolls } from 'store/speaker'
import PollingForm from 'views/admin/subjects/PollingForm/PollingForm'
import { colors } from 'utils/colors'

const emptyQuestion = {
  title: '',
  options: [
    { optId: nanoid(), opt: '' },
    { optId: nanoid(), opt: '' }
  ]
}
const pollInitialValues = {
  title: '',
  speakerId: '',
  questions: [emptyQuestion]
}

const AssignedPollRow = ({ id, poll, onRemove, classes }) => (
  <Box className={classes.assignedRow}>
    <Box className={classes.assignedRowText}>
      <Typography className={classes.assignedTitle}>{poll?.title ?? 'Untitled poll'}</Typography>
      <Typography className={classes.assignedSpeaker}>{poll?.speakerName}</Typography>
    </Box>
    <Tooltip title="Remove">
      <IconButton size="small" onClick={() => onRemove(id)}>
        <Close fontSize="small" />
      </IconButton>
    </Tooltip>
  </Box>
)

// Shared across EventForm/SubEventForm/TalkForm: lets an admin pick zero or more of the
// selected speakers' polls to attach (stored as a pollIds array on the parent form's values).
// Only one attached poll can be live at a time (enforced at start-time in PollView.js), so
// there's no execution order to manage here. Also owns the "create poll" modal, since all
// three forms wired up an identical instance of it around the old single-select dropdown.
const PollAssignment = ({
  conferenceId,
  speakerIds = [],
  confSpeakers = [],
  value = [],
  // The entity's own persisted assignment (from the event/session/talk doc, not the live
  // Formik value), used to tell "this entity's own poll" apart from "assigned to a different
  // entity" even after the admin toggles it off here — alreadyUsed on the poll doc itself
  // won't flip to false until the form is actually saved. Must come from the caller rather
  // than being snapshotted internally: this component only renders while havePolls is 'yes',
  // so it unmounts/remounts on every toggle, and by remount time the live `value` may already
  // reflect an unsaved removal, which would wrongly mark that poll as "assigned elsewhere".
  persistedPollIds = [],
  onChange,
  onSpeakerAdded,
  error
}) => {
  const classes = useStyles()
  const [speakerPolls, setSpeakerPolls] = useState([])
  const [loadingPolls, setLoadingPolls] = useState(false)
  const [openAddPollModal, setOpenAddPollModal] = useState(false)
  const [pollValues, setPollValues] = useState(pollInitialValues)
  const [confirmModal, setConfirmModal] = useState(false)

  const fetchSpeakerPolls = async (ids) => {
    setLoadingPolls(true)
    const polls = await getSpeakersAvailablePolls(conferenceId, ids)
    const pollsWithSpeaker = polls.map((poll) => {
      const speaker = confSpeakers?.find((s) => s.id === poll.speakerId)
      return { ...poll, speakerName: speaker?.fullName }
    })
    setSpeakerPolls(pollsWithSpeaker)
    // Drop any assigned poll whose speaker is no longer among the selected speakers
    // (mirrors the old single-select's auto-clear when its speaker was removed).
    const validIds = new Set(pollsWithSpeaker.map((poll) => poll.id))
    const prunedValue = value.filter((id) => validIds.has(id))
    if (prunedValue.length !== value.length) {
      onChange(prunedValue)
    }
    setLoadingPolls(false)
  }

  useEffect(() => {
    if (!conferenceId) return
    if (speakerIds?.length) {
      fetchSpeakerPolls(speakerIds)
    } else {
      setSpeakerPolls([])
      if (value.length) onChange([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conferenceId, speakerIds?.join(',')])

  const pollsById = useMemo(() => {
    const map = {}
    speakerPolls.forEach((poll) => {
      map[poll.id] = poll
    })
    return map
  }, [speakerPolls])

  const pollsBySpeaker = useMemo(() => {
    const groups = {}
    speakerPolls.forEach((poll) => {
      const key = poll.speakerId
      if (!groups[key]) groups[key] = { speakerName: poll.speakerName, polls: [] }
      groups[key].polls.push(poll)
    })
    return groups
  }, [speakerPolls])

  const togglePoll = (pollId) => {
    if (value.includes(pollId)) {
      onChange(value.filter((id) => id !== pollId))
    } else {
      onChange([...value, pollId])
    }
  }

  const handleRemove = (pollId) => onChange(value.filter((id) => id !== pollId))

  const handleCloseModal = (formValues) => {
    if (isEqual(pollValues, formValues)) {
      setOpenAddPollModal(false)
      setPollValues(pollInitialValues)
    } else {
      setConfirmModal(true)
    }
  }

  const handleCloseConfirmModal = () => {
    setConfirmModal(false)
    setOpenAddPollModal(false)
    setPollValues(pollInitialValues)
  }

  return (
    <Box className={classes.root}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography className={classes.sectionLabel}>Available Polls</Typography>
          <Box className={classes.panel}>
            {!speakerIds?.length ? (
              <FormHelperText className={classes.panelHelper}>
                Select at least one speaker to see their available polls.
              </FormHelperText>
            ) : loadingPolls ? (
              <Box className={classes.loadingBox}>
                <CircularProgress size={20} />
                <Typography>Loading polls...</Typography>
              </Box>
            ) : (
              <>
                {!speakerPolls.length && (
                  <Typography className={classes.panelHelper}>
                    None of the selected speakers have polls yet.
                  </Typography>
                )}
                {Object.entries(pollsBySpeaker).map(([speakerId, group]) => (
                  <Box key={speakerId} className={classes.speakerGroup}>
                    <Typography className={classes.speakerGroupLabel}>
                      {group.speakerName}
                    </Typography>
                    {group.polls.map((poll) => {
                      // A poll can only belong to one event/session/talk at a time. If it's
                      // already attached elsewhere (not to this entity's own assignment),
                      // it can't be picked here.
                      const isAssignedElsewhere =
                        poll.alreadyUsed && !persistedPollIds.includes(poll.id)
                      return (
                        <Box
                          key={poll.id}
                          className={`${classes.availableRow} ${
                            isAssignedElsewhere ? classes.availableRowDisabled : ''
                          }`}
                          onClick={() => !isAssignedElsewhere && togglePoll(poll.id)}>
                          <Checkbox
                            size="small"
                            checked={value.includes(poll.id)}
                            disabled={isAssignedElsewhere}
                            color="primary"
                          />
                          <Typography className={classes.availableRowLabel}>
                            {poll.title} ({poll.speakerName})
                          </Typography>
                          {isAssignedElsewhere && (
                            <Chip size="small" label="Assigned" className={classes.assignedChip} />
                          )}
                        </Box>
                      )
                    })}
                  </Box>
                ))}
                <Divider className={classes.divider} />
                <Box className={classes.createRow} onClick={() => setOpenAddPollModal(true)}>
                  <Add fontSize="small" />
                  <Typography className={classes.buttonItem}>Create poll</Typography>
                </Box>
              </>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography className={classes.sectionLabel}>Assigned Polls</Typography>
          <Box className={classes.panel}>
            {!value.length ? (
              <FormHelperText error={Boolean(error)} className={classes.panelHelper}>
                {error || 'No polls assigned yet. Select polls on the left to add them here.'}
              </FormHelperText>
            ) : (
              <Box className={classes.assignedList}>
                {value.map((pollId) => (
                  <AssignedPollRow
                    key={pollId}
                    id={pollId}
                    poll={pollsById[pollId]}
                    onRemove={handleRemove}
                    classes={classes}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      <Dialog
        open={openAddPollModal}
        maxWidth={false}
        PaperProps={{ className: classes.pollDialogPaper }}>
        <DialogContent className={classes.pollModalContent}>
          <PollingForm
            forApproval={true}
            editedPollId={null}
            pollValues={pollValues}
            speakerId={null}
            conferenceId={conferenceId}
            associateSpeaker={true}
            subjectId={null}
            handleCloseModal={handleCloseModal}
            handleCloseConfirmModal={() => setOpenAddPollModal(false)}
            onSuccess={async (pollId, newSpeakerId) => {
              const updatedSpeakerIds =
                newSpeakerId && !speakerIds.includes(newSpeakerId)
                  ? [...speakerIds, newSpeakerId]
                  : speakerIds
              if (newSpeakerId && !speakerIds.includes(newSpeakerId) && onSpeakerAdded) {
                onSpeakerAdded(newSpeakerId)
              }
              onChange([...value, pollId])
              await fetchSpeakerPolls(updatedSpeakerIds)
              setOpenAddPollModal(false)
              setPollValues(pollInitialValues)
            }}
          />
        </DialogContent>
      </Dialog>
      <Dialog fullWidth maxWidth="sm" open={confirmModal} onClose={() => setConfirmModal(false)}>
        <Box className={classes.warningModalHeader}>
          <Warning className={classes.warningIcon} />
          <Typography className={classes.warningTitle}>Warning</Typography>
        </Box>
        <DialogContent className={classes.modalContent}>
          <Typography variant="body2">
            Any unsaved data will be lost. Press CANCEL to go back and save these changes, or OK to
            discard these changes.
          </Typography>
          <Box className={classes.actions}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={handleCloseConfirmModal}>
              ok
            </Button>
            <Button size="medium" onClick={() => setConfirmModal(false)}>
              cancel
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default PollAssignment

const useStyles = makeStyles({
  root: {
    width: '100%'
  },
  sectionLabel: {
    fontSize: '13px',
    fontWeight: 600,
    letterSpacing: '0.5px',
    color: '#606060',
    marginBottom: '4px'
  },
  panel: {
    border: '1px solid #dedede',
    borderRadius: '8px',
    padding: '8px',
    minHeight: '96px',
    maxHeight: '320px',
    overflowY: 'auto'
  },
  panelHelper: {
    padding: '8px'
  },
  loadingBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '8px'
  },
  speakerGroup: {
    marginBottom: '4px'
  },
  speakerGroupLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: colors.indigo,
    padding: '6px 8px 2px'
  },
  availableRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    borderRadius: '6px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  },
  availableRowDisabled: {
    cursor: 'default',
    opacity: 0.65,
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  availableRowLabel: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  assignedChip: {
    height: '20px',
    fontSize: '11px',
    fontWeight: 600,
    backgroundColor: '#fbe9e7',
    color: '#bf360c'
  },
  divider: {
    margin: '8px 0'
  },
  createRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
    padding: '6px 8px',
    cursor: 'pointer',
    color: colors.indigo
  },
  buttonItem: {
    color: colors.indigo,
    fontWeight: 600
  },
  assignedList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  assignedRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 8px',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    backgroundColor: '#fff'
  },
  assignedRowText: {
    flex: 1,
    minWidth: 0
  },
  assignedTitle: {
    fontSize: '14px',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  assignedSpeaker: {
    fontSize: '12px',
    color: '#757575'
  },
  warningModalHeader: {
    padding: '20px 20px 10px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  warningIcon: {
    color: '#ffa726'
  },
  warningTitle: {
    fontSize: '16px',
    fontWeight: 'bold'
  },
  modalContent: {
    padding: '4px 16px 4px 22px'
  },
  actions: {
    marginTop: '20px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginBottom: '10px'
  },
  pollDialogPaper: {
    width: '92vw',
    maxWidth: '1400px',
    height: '92vh',
    maxHeight: '92vh'
  },
  pollModalContent: {
    overflowX: 'hidden',
    // The form itself now owns the scrolling region (see PollingForm's scrollArea) so it
    // can keep its header/footer pinned in place; this container just fills the paper.
    overflowY: 'hidden',
    // No top padding: PollingForm's own header already carries top spacing, and padding
    // here would leave a gap above it once it's pinned at the scroll edge.
    // !important beats MUI's default .MuiDialogContent-root padding at equal specificity.
    padding: '0px 30px 30px !important'
  }
})
